/**
 * One dispatch as a card, and the two bits of presentation that go with it.
 *
 * Shared by `/news` (the full board) and the landing page's "What's new"
 * strip. They are the same card deliberately: a reader who clicks through
 * from the landing page should land on a board that looks like where they
 * came from, and two copies of this markup would drift the moment either one
 * gained a field — which is exactly what the header image just did.
 *
 * ─── Nothing here trusts its input ──────────────────────────────────────────
 * Every field on a `News` row is admin-editable and core never sanitises it,
 * so the text is escaped and `imageUrl` is run through `safeImageUrl` rather
 * than dropped into `src` as-is. The upload endpoint returns `/uploads/...`,
 * but the admin panel also lets an operator PASTE a URL into that field, and
 * a pasted string is a string.
 */
import { escapeAttr, escapeHtml } from './landing-escape';

/** The shape both callers have — `NewsItem` from `landing-cms`, or an article. */
export interface NewsCardData {
  slug: string;
  title: string;
  summary: string;
  tag: string;
  imageUrl: string | null;
  publishedAt: string | null;
}

/**
 * A URL that is safe to put in `src`, or null.
 *
 * Accepts the two things an operator legitimately produces: the upload
 * endpoint's own relative `/uploads/...` key, and an absolute http(s) URL for
 * an image hosted elsewhere. Everything else — `javascript:`, `data:`, a
 * protocol-relative `//host` — resolves to null and the card simply has no
 * picture. A `javascript:` URL in `src` is inert in every current browser,
 * but "inert today" is not the same as "cannot be rendered", and this field
 * reaches the page from the admin panel with no validation behind it.
 */
export function safeImageUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  const trimmed = url.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith('//')) return null;
  if (trimmed.startsWith('/')) return trimmed;
  return /^https?:\/\//i.test(trimmed) ? trimmed : null;
}

/**
 * A date the reader can parse at a glance, in THEIR locale.
 *
 * Returns an empty string for a missing date rather than "Invalid Date": an
 * article can be published with no `publishedAt`, and a broken-looking date is
 * worse than none.
 */
export function shortDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/**
 * The card.
 *
 * The image is OPTIONAL and its absence is not a gap: a card with no
 * `imageUrl` emits no `<img>` at all and the text closes up, rather than
 * reserving an empty box. `alt=""` because the title sits directly beneath it
 * — a screen reader announcing the headline twice is noise, and the picture
 * carries no information the card does not already say in words.
 */
export function newsCardMarkup(item: NewsCardData): string {
  const image = safeImageUrl(item.imageUrl);
  const thumb = image
    ? `<span class="lp-news-thumb">
        <img src="${escapeAttr(image)}" alt="" loading="lazy" decoding="async">
      </span>`
    : '';

  return `
    <a class="lp-news-card" href="/news/${encodeURIComponent(item.slug)}"
       data-news-slug="${escapeAttr(item.slug)}">
      ${thumb}
      <span class="lp-news-tag">${escapeHtml(item.tag)}</span>
      <span class="lp-news-date">${escapeHtml(shortDate(item.publishedAt))}</span>
      <span class="lp-news-title">${escapeHtml(item.title)}</span>
      <span class="lp-news-summary">${escapeHtml(item.summary)}</span>
      <span class="lp-news-more">Read it →</span>
    </a>`;
}
